import { QuestionCircleOutlined, SettingOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Carousel,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Tooltip,
  Typography,
  Checkbox,
  InputNumber,
  ColorPicker,
  Select,
  Tabs,
  Switch
} from "antd";
import { getCookie, setCookie } from "cookies-next";
import Hls from "hls.js";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { FaRegPlayCircle } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import { makeApiRequest } from '../../../../shared/api';
import styles from "./style.module.scss";
import { useDispatch } from 'react-redux';
import { setOnboardingComplete } from '../../../../shared/store/onboardSlice';

const { Title, Text } = Typography;
const { Option } = Select;

const CreatingBot = ({ setCurrent, setAvatarId }) => {
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAdditionalSettingsVisible, setIsAdditionalSettingsVisible] = useState(false);
  const [facebotIntroScript, setFacebotIntroScript] = useState("");
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedContacts, setSelectedContacts] = useState(["name", "email"]);
  const [isHovered, setIsHovered] = useState(false);
  const [facebotName, setFacebotName] = useState("");
  const [enableMike, setEnableMike] = useState(true);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [salesforceProject, setSalesforceProject] = useState("");
  const [salesforceUrl, setSalesforceUrl] = useState("");
  const [enableSalesforceIntegration, setEnableSalesforceIntegration] = useState(false);
  const [form] = Form.useForm();
  const [enableNumberInput, setEnableNumberInput] = useState(false);
  const [enableWidgetPrompt, setEnableWidgetPrompt] = useState(false);
  const [widgetPromptText, setWidgetPromptText] = useState("I'm here to assist you!");
  const [contactPromptPoint, setContactPromptPoint] = useState(null);
  const KB_ID = getCookie("fb_kb_id");
  const [widgetTitle, setWidgetTitle] = useState('');
  const [themeColor, setThemeColor] = useState('#1890ff');
  const [widgetTextColor, setWidgetTextColor] = useState('#000000');
  const [knowledgeBaseList, setKnowledgeBaseList] = useState([]);
  const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState(null);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
  const [widgetPosition, setWidgetPosition] = useState('right');
  const [isFacebotNameEdited, setIsFacebotNameEdited] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    getAllAvatars();
  }, []);

  const getAllAvatars = async() => {
    try {
      setLoading(true);
      const response = await makeApiRequest("GET", "avatars");
      setAvatars(response);
      setSelectedAvatar(response[0] || null);
      setFacebotName("");
    } catch (error) {
      setAvatars([]);
      setSelectedAvatar(null);
    } finally {
      setLoading(false);
    }
  };
  const getAllKb = useCallback(async() => {
    try {
      setLoading(true);
      const response = await makeApiRequest("GET", "/kb");
      
      if (response && Array.isArray(response)) {
        setKnowledgeBaseList(response);
        if (!selectedKnowledgeBaseId && response.length > 0) {
          setSelectedKnowledgeBaseId(response[0].kb_id);
        }
      } else {
        console.error("API response is not an array:", response);
        setKnowledgeBaseList([]);
        setSelectedKnowledgeBaseId(null);
      }
    } catch (err) {
      console.error("Error fetching knowledge bases:", err);
      setKnowledgeBaseList([]);
      setSelectedKnowledgeBaseId(null);
    } finally {
      setLoading(false);
    }
  }, [selectedKnowledgeBaseId]);
  
  // Add useEffect to fetch knowledge bases
  useEffect(() => {
    getAllKb();
  }, [getAllKb]);

  const chunkArray = (arr, size) => {
    return arr.reduce((acc, _, i) => {
      if (i % size === 0) acc.push(arr.slice(i, i + size));
      return acc;
    }, []);
  };

  const chunkSize = isMobile ? 3 : 5;
  const chunkedAvatars = chunkArray(avatars, chunkSize);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleAvatarSelect = (avatar) => {
    setSelectedAvatar(avatar);
    setAvatarId(avatar.avatar_id);
  };

  const handleCarouselChange = (current) => {
    setCurrentSlide(current);
  };

  useEffect(() => {
    if (selectedAvatar && chunkedAvatars.length > 0) {
      let found = false;
      chunkedAvatars[currentSlide].forEach((avatar) => {
        if (avatar.avatar_image === selectedAvatar.avatar_image) {
          found = true;
        }
      });

      if (!found) {
        setSelectedAvatar(chunkedAvatars[currentSlide][0] || null);

      }
    }
  }, [currentSlide, chunkedAvatars, selectedAvatar]);

  const handleFinishSetup = () => {
    form.validateFields()
      .then((values) => {
        CreateFaceBot(values);
      })
      .catch((errorInfo) => {
        console.log('Failed:', errorInfo);
      });
  };

  const handleAIGenerate = () => {
    if (facebotIntroScript.trim()) {
      // Clear current script first
      setFacebotIntroScript("");
    }
  
    // Simulate AI generation (replace with real API call if needed)
    const generatedText = "Hi there! I'm your friendly FaceBot, here to assist you.";
    setTimeout(() => {
      setFacebotIntroScript(generatedText);
    }, 300); // optional delay to simulate API
  };
  

  const CreateFaceBot = async(values) => {
    try {
      setLoading(true);

      const fields = enableNumberInput
        ? selectedContacts.map((attr, index) => ({
            label:
              attr === "name"
                ? "Name"
                : attr === "email"
                ? "Email"
                : "Phone Number",
            type:
              attr === "email"
                ? "email"
                : attr === "phone_number"
                ? "phone"
                : "string",
            required: attr === "name" || attr === "email", // Name and email are required
            position: index + 1,
          }))
        : [];

      const payload = {
        kb_id: selectedKnowledgeBase,
        avatar_id: selectedAvatar?.avatar_id,
        facebot_name: facebotName,
        intro_script: facebotIntroScript,
        audio_input: enableMike,
        facebot_title: widgetTitle,
        theme_color: themeColor,
        contact_prompt_point: enableNumberInput ? contactPromptPoint : 0,
        contact_fields: enableNumberInput ? fields : [],
        enable_contact_fields: enableNumberInput,
        notification_email_id: notificationEmail?.trim() || "",
        salesforce_project: enableSalesforceIntegration? salesforceProject : "",
        salesforce_url: enableSalesforceIntegration? salesforceUrl: "",
        enable_widget_prompt: enableWidgetPrompt,
        tooltip_text: enableWidgetPrompt ? widgetPromptText : null,
        tooltip_text_color: enableWidgetPrompt ? widgetTextColor : null,
        widget_placement: widgetPosition,
      };
      console.log('Payload being sent:', payload);

      const response = await makeApiRequest("POST", "facebots", payload);

      setCookie("fb_id", response?.facebot_id);
      dispatch(setOnboardingComplete(true)); // <-- Add this line
      setCurrent(4);
    } catch (error) {
      console.error("Error creating FaceBot:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (!selectedAvatar) {
      return <Text>No avatar selected.</Text>;
    }

    const videoUrl = selectedAvatar.sample_video;

    if (!videoUrl) {
      return <Text>No video available for this avatar.</Text>;
    }

    const videoType = videoUrl.endsWith(".m3u8")
      ? "application/vnd.apple.mpegurl"
      : "video/mp4";

    return (
      <video
        ref={videoRef}
        controls
        width="100%"
        height="auto"
        style={{ backgroundColor: "black" }}
      >
        <source src={videoUrl} type={videoType} />
        Your browser does not support the video tag.
      </video>
    );
  };

  useEffect(() => {
    let hls = null;
    if (
      isPreviewModalVisible &&
      videoRef.current &&
      selectedAvatar?.sample_video?.endsWith(".m3u8")
    ) {
      const video = videoRef.current;
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(selectedAvatar.sample_video);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play();
        });
        hls.on(Hls.Events.ERROR, function(event, data) {
          if (data.fatal) {
            switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
            }
          }
        });
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = selectedAvatar.sample_video;
        video.addEventListener("loadedmetadata", () => {
          video.play();
        });
      } else {
        console.error("HLS is not supported in this browser.");
      }
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [isPreviewModalVisible, selectedAvatar]);

  return (
    <div className={styles.container}>
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={14}>
          <Title level={3} style={{ fontWeight: "normal" }}>
            Yaa! almost there! - Create FaceBot
          </Title>
          <Text className={styles.description}>
            Setting up your FaceBot allows you to create a personalised digital
            representation of your product or service within virtual
            environments. Customising your FaceBot gives you the opportunity to
            showcase your unique style and identity. Choose from a variety of
            features to make your FaceBot truly stand out.
          </Text>
        </Col>
      </Row>

      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={14}>
          <Title level={3} style={{ fontWeight: "normal", marginTop: 50 }}>
            Select FaceBot
          </Title>
          <Text className={styles.description}>
            Setting up your FaceBot allows you to create a personalised digital
            representation of your product or service within virtual
            environments. Customising your FaceBot gives you the opportunity to
            showcase your unique style and identity. Choose from a variety of
            features to make your FaceBot truly stand out.
          </Text>
        </Col>
      </Row>

      <div style={{ maxWidth: 880, margin: "auto", padding: 20 }}>
        <Title level={4} style={{ fontWeight: "normal", marginTop: 30 }}>
          Selected FaceBot
        </Title>
        <Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            {isLoading ? (
              <Skeleton height={350} width={350} borderRadius="4px" />
            ) : (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginBottom: 10,
                  marginLeft: isMobile ? 0 : "50px",
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Avatar
                  size={350}
                  src={selectedAvatar?.avatar_image}
                  shape="square"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    cursor: "pointer",
                    filter: isHovered ? "blur(2px)" : "none",
                    transition: "filter 0.1s ease-in-out",
                  }}
                  onClick={() => setIsPreviewModalVisible(true)}
                />

                {isHovered && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      fontSize: "3rem",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      cursor: "pointer",
                      transition: "background-color 0.3s ease",
                    }}
                    onClick={() => setIsPreviewModalVisible(true)}
                  >
                    <FaRegPlayCircle />
                  </div>
                )}
              </div>
            )}
            <div style={{ maxWidth: 900, margin: "auto" }}>
              <Carousel
                dots={false}
                arrows
                draggable
                style={{
                  marginTop: 40,
                  width: isMobile ? "18rem" : "28rem",
                  marginLeft: isMobile ? 0 : "0",
                  marginRight: isMobile ? 0 : "auto",
                }}
                afterChange={handleCarouselChange}
                current={currentSlide}
              >
                {chunkedAvatars.map((group, index) => (
                  <div key={index}>
                    <Row justify={isMobile ? "start" : "center"} gutter={16}>
                      {group.map((avatar, idx) => (
                        <Col key={idx}>
                          {isLoading ? (
                            <Skeleton shape="square" height={64} width={64} />
                          ) : (
                            <Avatar
                              size={64}
                              src={avatar.avatar_image}
                              shape="square"
                              onClick={() => handleAvatarSelect(avatar)}
                              style={{
                                cursor: "pointer",
                                border:
                                  selectedAvatar?.avatar_image ===
                                  avatar.avatar_image
                                    ? "2px solid #2fcc71"
                                    : "none",
                              }}
                            />
                          )}
                        </Col>
                      ))}
                    </Row>
                  </div>
                ))}
              </Carousel>
            </div>
          </Col>
          <Col
            xs={24}
            sm={12}
            md={10}
            lg={10}
            xl={10}
            style={{ marginLeft: 60 }}
          >
            {isLoading ? (
              <>
                <Skeleton height={24} width={150} />
                <br />
              </>
            ) : (
              <Form form={form} layout="vertical">
                {/* Basic Settings */}
                <Form.Item
                  name="facebotName"
                  label={
                    <span>
                      FaceBot Name
                      <Tooltip title="This name will represent your AI FaceBot across the platform.">
                        <QuestionCircleOutlined
                          style={{ color: "#1890ff", paddingLeft: 5 }}
                        />
                      </Tooltip>
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please enter a name for your FaceBot!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter FaceBot Name"
                    value={facebotName}
                    allowClear
                    onChange={(e) => {
                      setFacebotName(e.target.value);
                      setIsFacebotNameEdited(true);
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="facebotIntroScript"
                  label={
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span>FaceBot Intro Script</span>
                      <Tooltip title="This is the introduction message your FaceBot will use when greeting users.">
                        <QuestionCircleOutlined style={{ color: "#1890ff" }} />
                      </Tooltip>
                      <Tooltip
                        title={
                          facebotIntroScript.trim()
                            ? "Clear to use AI"
                            : "Generate with AI"
                        }
                      >
                        <Button
                          type="default"
                          size="small"
                          onClick={handleAIGenerate}
                          disabled={!!facebotIntroScript.trim()}
                          style={{
                            height: 22,
                            padding: "0 8px",
                            fontSize: 12,
                            lineHeight: 1,
                            marginTop: -2, // adjust vertical alignment
                          }}
                        >
                          AI Generate ✨
                        </Button>
                      </Tooltip>
                    </div>
                  }
                  rules={[
                    {
                      required: true,
                      message: "Please enter an introduction script!",
                    },
                    {
                      max: 500,
                      message:
                        "Intro script cannot be longer than 500 characters!",
                    },
                  ]}
                >
                  <Input.TextArea
                    placeholder="Enter introduction script for your FaceBot"
                    value={facebotIntroScript}
                    allowClear
                    rows={4}
                    onChange={(e) => setFacebotIntroScript(e.target.value)}
                  />
                </Form.Item>
                <Form.Item
                  name="audio_input"
                  label={
                    <span style={{ fontWeight: 500 }}>Enable Microphone</span>
                  }
                  valuePropName="checked"
                  style={{ marginBottom: 16 }}
                >
                  <Switch
                    defaultChecked={true}
                    disabled={true}
                    checkedChildren="Enabled"
                    unCheckedChildren="Disabled"
                  />
                </Form.Item>
                <Form.Item
                  name="knowledge_base"
                  label={
                    <span style={{ fontWeight: 500 }}>
                      Select Existing Knowledge Base
                    </span>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Select
                    placeholder="Select a knowledge base"
                    onChange={(value) => setSelectedKnowledgeBase(value)}
                    style={{ width: "100%" }}
                    isLoading={isLoading}
                  >
                    {knowledgeBaseList.map((kb) => (
                      <Option key={kb.kb_id} value={kb.kb_id}>
                        {kb.kb_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <div style={{ marginTop: 24 }}>
                  <Space size="middle">
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleFinishSetup}
                      disabled={!facebotName || !facebotIntroScript}
                    >
                      Finish Setup
                    </Button>
                    <Button
                      type="default"
                      size="large"
                      icon={<SettingOutlined />}
                      onClick={() => setIsAdditionalSettingsVisible(true)}
                    >
                      Additional Settings
                    </Button>
                  </Space>
                </div>
              </Form>
            )}
          </Col>
          <Modal
            title="Additional Settings"
            open={isAdditionalSettingsVisible}
            onCancel={() => setIsAdditionalSettingsVisible(false)}
            width={600}
            footer={[
              <Button
                key="cancel"
                onClick={() => setIsAdditionalSettingsVisible(false)}
              >
                Cancel
              </Button>,
              <Button
                key="save"
                type="primary"
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsAdditionalSettingsVisible(false);
                  });
                }}
              >
                Save Changes
              </Button>,
            ]}
          >
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="📝 Core Settings" key="1">
                <Form.Item name="enableContactForm">
                  <Checkbox
                    onChange={(e) => {
                      setEnableNumberInput(e.target.checked);
                      if (e.target.checked) {
                        setSelectedContacts(["name", "email"]);
                        form.setFieldsValue({
                          contact_attributes: ["name", "email"],
                        });
                      } else {
                        setSelectedContacts([]);
                        form.setFieldsValue({ contact_attributes: [] });
                        setContactPromptPoint(null);
                      }
                    }}
                  >
                    Enable Contact Form
                  </Checkbox>
                </Form.Item>

                {enableNumberInput && (
                  <>
                    <Form.Item
                      name="contact_prompt_point"
                      label="Contact Form Position"
                      rules={[
                        {
                          required: true,
                          message:
                            "Please enter a numerical value (greater than 0)",
                          type: "number",
                          min: 1,
                          max: 20,
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={1}
                        max={20}
                        placeholder="Update Contact form Position number"
                        size="large"
                        onChange={(value) => setContactPromptPoint(value)}
                      />
                    </Form.Item>

                    <Form.Item
                      name="contact_attributes"
                      label="Select Contact Attributes"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value || value.length < 2) {
                              return Promise.reject(
                                "Please select at least name and email fields"
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select contact form fields"
                        style={{ width: "100%" }}
                        size="large"
                        value={selectedContacts}
                        onChange={(values) => {
                          const requiredFields = ["name", "email"];
                          const newSelection = [
                            ...new Set([...requiredFields, ...values]),
                          ];
                          setSelectedContacts(newSelection);
                        }}
                        options={[
                          {
                            value: "name",
                            label: "Name *",
                            disabled: true,
                          },
                          {
                            value: "email",
                            label: "Email *",
                            disabled: false,
                          },
                          { value: "phone_number", label: "Phone Number" },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      name="notification_email_id"
                      label="Email to Notify"
                      tooltip="Email address where contact form submissions will be sent"
                      initialValue=""
                      rules={[
                        {
                          type: "email",
                          message: "Please enter a valid email address",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter email address"
                        onChange={(e) => setNotificationEmail(e.target.value)}
                      />
                    </Form.Item>
                    <Form.Item
                      name="enable_salesforce_crm"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Enable Salesforce CRM Integration
                        </span>
                      }
                      tooltip="Enable to sync contact form submissions with Salesforce"
                      valuePropName="checked"
                      style={{ marginBottom: 16 }}
                    >
                      <Switch
                        onChange={(checked) =>
                          setEnableSalesforceIntegration(checked)
                        }
                        defaultChecked={false}
                      />
                    </Form.Item>
                    {enableSalesforceIntegration && (
                      <>
                        <Form.Item
                          name="salesforce_project"
                          label={
                            <span style={{ fontWeight: 500 }}>
                              Salesforce Project
                            </span>
                          }
                          tooltip="Enter your Salesforce project name"
                          style={{ marginBottom: 16 }}
                          rules={[
                            {
                              required: true,
                              message:
                                "Please enter your Salesforce Project name",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter project name"
                            onChange={(e) => {
                              form.setFieldsValue({
                                salesforce_project: e.target.value,
                              });
                              setSalesforceProject(e.target.value);
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          name="salesforce_url"
                          label={
                            <span style={{ fontWeight: 500 }}>
                              Salesforce URL
                            </span>
                          }
                          tooltip="Enter your Salesforce endpoint URL"
                          style={{ marginBottom: 16 }}
                          rules={[
                            {
                              required: true,
                              message: "Please enter your Salesforce URL",
                            },
                          ]}
                        >
                          <Input
                            placeholder="https://your-instance.salesforce.com/Id/webToLead"
                            onChange={(e) => {
                              form.setFieldsValue({
                                salesforce_url: e.target.value,
                              });
                              setSalesforceUrl(e.target.value);
                            }}
                          />
                        </Form.Item>
                      </>
                    )}
                  </>
                )}
                <Form.Item name="enableWidgetPrompt" style={{ marginTop: 16 }}>
                  <Checkbox
                    checked={enableWidgetPrompt}
                    onChange={(e) => setEnableWidgetPrompt(e.target.checked)}
                  >
                    Enable Widget Prompt Text
                  </Checkbox>
                </Form.Item>
                {enableWidgetPrompt && (
                  <>
                    <Form.Item
                      name="widgetPromptText"
                      label="Widget Prompt Text"
                      initialValue="I'm here to assist you!"
                      tooltip="This text will be shown as a prompt in the widget"
                    >
                      <Input
                        placeholder="Enter widget prompt text"
                        defaultValue="I'm here to assist you!"
                        value={widgetPromptText}
                        onChange={(e) => setWidgetPromptText(e.target.value)}
                        allowClear
                      />
                    </Form.Item>

                    <Form.Item
                      name="widgetTextColor"
                      label="Widget Text Color"
                      tooltip="Choose the color for your widget prompt text"
                    >
                      <ColorPicker
                        showText
                        size="large"
                        value={widgetTextColor}
                        defaultValue="#000000"
                        onChange={(color) =>
                          setWidgetTextColor(color.toHexString())
                        }
                        presets={[
                          {
                            label: "Recommended Colors",
                            colors: [
                              "#000000",
                              "#FFFFFF",
                              "#1890ff",
                              "#6739b6",
                              "#ff6b81",
                              "#52c41a",
                              "#faad14",
                              "#13c2c2",
                              "#eb2f96",
                            ],
                          },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      name="widget_placement"
                      label={
                        <span style={{ fontWeight: 500 }}>Widget Position</span>
                      }
                      initialValue="right"
                      tooltip="Select the position where the widget should appear on the website"
                    >
                      <Select
                        defaultValue="right"
                        onChange={(value) => setWidgetPosition(value)}
                        options={[
                          { value: "right", label: "Right" },
                          { value: "left", label: "Left" },
                        ]}
                      />
                    </Form.Item>
                  </>
                )}
              </Tabs.TabPane>
              <Tabs.TabPane tab="🎨 Appearance" key="2">
                <Form.Item
                  name="widgetTitle"
                  label="Widget Title"
                  tooltip="This name will represent your top of the widget"
                >
                  <Input
                    placeholder="Enter Widget Title"
                    value={widgetTitle}
                    allowClear
                    onChange={(e) => setWidgetTitle(e.target.value)}
                  />
                </Form.Item>

                <Form.Item
                  name="themeColor"
                  label="Widget Theme Color"
                  tooltip="This will represent your widget theme color"
                >
                  <ColorPicker
                    showText
                    size="large"
                    value={themeColor}
                    defaultValue="#1890ff"
                    onChange={(color) => setThemeColor(color.toHexString())}
                    presets={[
                      {
                        label: "Recommended Colors",
                        colors: [
                          "#1890ff",
                          "#6739b6",
                          "#ff6b81",
                          "#52c41a",
                          "#faad14",
                          "#13c2c2",
                          "#eb2f96",
                          "#722ed1",
                          "#fadb14",
                        ],
                      },
                    ]}
                  />
                </Form.Item>
              </Tabs.TabPane>
            </Tabs>
          </Modal>
        </Row>
      </div>

      <Modal
        title="Preview"
        open={isPreviewModalVisible}
        onCancel={() => setIsPreviewModalVisible(false)}
        centered
        footer={
          <>
            <Button
              type="primary"
              onClick={() => setIsPreviewModalVisible(false)}
            >
              Close
            </Button>
          </>
        }
        afterOpen={() => {
          if (videoRef.current) {
            if (selectedAvatar?.sample_video?.endsWith(".m3u8")) {
              if (Hls.isSupported()) {
                const hls = new Hls();
                hls.loadSource(selectedAvatar.sample_video);
                hls.attachMedia(videoRef.current);
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                  videoRef.current.play();
                });
                hls.on(Hls.Events.ERROR, function (event, data) {
                  if (data.fatal) {
                    switch (data.type) {
                      case Hls.ErrorTypes.NETWORK_ERROR:
                        hls.startLoad();
                        break;
                      case Hls.ErrorTypes.MEDIA_ERROR:
                        console.error(
                          "fatal media error encountered, trying to recover"
                        );
                        hls.recoverMediaError();
                        break;
                      default:
                        hls.destroy();
                        console.error(
                          "Fatal error encountered, cannot recover."
                        );
                        break;
                    }
                  }
                });
              } else if (
                videoRef.current.canPlayType("application/vnd.apple.mpegurl")
              ) {
                videoRef.current.src = selectedAvatar.sample_video;
                videoRef.current.addEventListener("loadedmetadata", () => {
                  videoRef.current.play();
                });
              } else {
                console.error("HLS is not supported in this browser.");
              }
            } else {
              videoRef.current.play();
            }
          }
        }}
      >
        <div style={{ maxWidth: "600px", margin: "auto", padding: 3 }}>
          <div style={{ width: "100%", maxWidth: "600px" }}>
            {renderPreview()}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CreatingBot;